// Stage 200: GPP Laughter Module — frontend API client.
//
// Mirrors frontend/src/api/gadfly.ts in style. Types are DELIBERATELY
// duplicated rather than imported from the worker. The cross-package
// air gap is duplication.
//
// IMPORTANT: scripts/verify-laughter-boundaries.cjs asserts that
// frontend/src/components/ChatTranscript.tsx never imports this module.
// Laughter engrams are operator-only accumulation; they must never reach
// end users in the chat transcript.

import { apiGet, apiJson } from './client';
import type {
  LaughterEngramInput,
  LaughterSubclass,
  ReframeSubject,
  WitnessReframeCue,
} from '../modules/Laughter/types';

export interface DepositResponse {
  engramId: string;
  duplicate: boolean;
}

export interface CountResponse {
  total: number;
  perSubclass: Record<LaughterSubclass, number>;
  /** Stage 201-S: per-reframe-subject totals. */
  perSubject: Record<ReframeSubject, number>;
  lastFiredAt: string | null;
}

/** Stage 201-S + 202-W: result of the witness-reframe detector. */
export interface WitnessDetectResult {
  matched: boolean;
  cues: WitnessReframeCue[];
  normalisedText: string;
  /** Stage 202-W: which detector produced this verdict. */
  detector: 'lexical-heuristic-v1' | 'semantic-v1';
  /** Stage 202-W: present only when detector === 'semantic-v1'. */
  semantic?: {
    kind:
      | 'recognition'
      | 'surprise'
      | 'concession'
      | 'flip'
      | 'understanding'
      | 'none';
    confidence: 'high' | 'medium' | 'low';
    grounds: string;
  };
}

export interface StoredLaughterEngram extends LaughterEngramInput {
  id: string;
  insertedAt: number;
}

export interface ListResponse {
  engrams: StoredLaughterEngram[];
  nextCursor: string | null;
}

export interface LaughterHealth {
  ok: boolean;
  service: string;
  version: string;
  enabled: boolean;
  region: string;
  timestamp: string;
}

/** GET /api/laughter/healthcheck — public, no auth. */
export const laughterHealthcheck = () =>
  apiGet<LaughterHealth>('/api/laughter/healthcheck');

/** POST /api/laughter/deposit — admin-only. Called by the LaughterSubscriber. */
export const laughterDeposit = (engram: LaughterEngramInput) =>
  apiJson<DepositResponse>('POST', '/api/laughter/deposit', engram);

/** GET /api/laughter/count — admin-only. Polled by the LaughterTab counter. */
export const laughterCount = () =>
  apiGet<CountResponse>('/api/laughter/count');

/** GET /api/laughter/list — admin-only. Used by the LaughterTab inspector. */
export const laughterList = (params: {
  subclass?: LaughterSubclass;
  subject?: ReframeSubject;
  cursor?: string;
  limit?: number;
}) => {
  const q = new URLSearchParams();
  if (params.subclass) q.set('subclass', params.subclass);
  if (params.subject) q.set('subject', params.subject);
  if (params.cursor) q.set('cursor', params.cursor);
  if (params.limit) q.set('limit', String(params.limit));
  return apiGet<ListResponse>(
    `/api/laughter/list${q.toString() ? `?${q.toString()}` : ''}`,
  );
};

/** POST /api/laughter/witness-detect — admin-only. Lexical pre-filter +
 *  optional Stage-202-W semantic pass (when conversationId is supplied and
 *  the semantic flag is on). */
export const laughterWitnessDetect = (body: {
  text: string;
  conversationId?: string;
  turnId?: string;
}) => apiJson<WitnessDetectResult>('POST', '/api/laughter/witness-detect', body);

/** Stage 202-J: verdict of the two-pass shared-interpretive-ground read. */
export interface SharedGroundVerdict {
  ok: boolean;
  shifted: boolean;
  bothParties: boolean;
  before: string;
  after: string;
  confidence: 'high' | 'medium' | 'low';
}

/** POST /api/laughter/shared-ground-detect — admin-only. Returns
 *  { ok:false, ... } (200) when the shared-ground flag is off. */
export const laughterSharedGroundDetect = (body: { conversationId: string }) =>
  apiJson<SharedGroundVerdict>(
    'POST',
    '/api/laughter/shared-ground-detect',
    body,
  );
